import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dropzone", "input", "preview", "filename", "submit"]
  static classes = ["dragover"]
  static values = { autoSubmit: Boolean }

  declare readonly dropzoneTarget: HTMLElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly previewTarget: HTMLElement
  declare readonly filenameTarget: HTMLElement
  declare readonly submitTarget: HTMLButtonElement
  declare readonly dragoverClasses: string[]
  declare readonly autoSubmitValue: boolean

  connect() {
    this.bindEvents()
  }

  private bindEvents() {
    // Prevent default drag behaviors
    ;["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, this.preventDefaults.bind(this), false)
    })

    // Highlight on drag over
    ;["dragenter", "dragover"].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, this.highlight.bind(this), false)
    })

    // Remove highlight on drag leave or drop
    ;["dragleave", "drop"].forEach(eventName => {
      this.dropzoneTarget.addEventListener(eventName, this.unhighlight.bind(this), false)
    })

    // Handle dropped files
    this.dropzoneTarget.addEventListener("drop", this.handleDrop.bind(this), false)
  }

  private preventDefaults(e: Event) {
    e.preventDefault()
    e.stopPropagation()
  }

  private highlight() {
    this.dropzoneTarget.classList.add(...this.dragoverClasses)
  }

  private unhighlight() {
    this.dropzoneTarget.classList.remove(...this.dragoverClasses)
  }

  private handleDrop(e: DragEvent) {
    const dt = e.dataTransfer
    if (!dt) return

    const files = dt.files
    this.handleFiles(files)
  }

  handleFileSelect(e: Event) {
    const input = e.target as HTMLInputElement
    if (input.files) {
      this.handleFiles(input.files)
    }
  }

  private handleFiles(files: FileList) {
    if (files.length === 0) return

    const file = files[0]
    // Accepted extensions come from the file input's `accept` attribute so
    // each consumer (contact import, library, …) controls its own allowlist
    // without forking this controller. Falls back to the legacy CSV/XLSX
    // list if `accept` isn't set.
    const acceptAttr = this.inputTarget.getAttribute("accept") || ""
    const validExtensions = acceptAttr
      .split(",")
      .map(s => s.trim().toLowerCase())
      .filter(s => s.startsWith("."))
    const fallbackExtensions = [".csv", ".xlsx", ".xls"]
    const allowedExtensions = validExtensions.length > 0 ? validExtensions : fallbackExtensions
    const extension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase()

    if (!allowedExtensions.includes(extension)) {
      alert(`Format de fichier non supporté. Utilisez ${allowedExtensions.join(", ")}.`)
      return
    }

    // Update the file input
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    this.inputTarget.files = dataTransfer.files

    // Show preview
    this.showPreview(file)
  }

  private showPreview(file: File) {
    if (this.hasPreviewTarget) {
      this.previewTarget.classList.remove("hidden")
    }
    if (this.hasFilenameTarget) {
      this.filenameTarget.textContent = file.name
    }
    if (this.hasSubmitTarget) {
      this.submitTarget.disabled = false
    }

    // Auto-submit the form if enabled
    if (this.autoSubmitValue) {
      const form = this.element.closest("form") || this.element.querySelector("form")
      if (form) {
        this.showUploadingState(file)
        form.requestSubmit()
      }
    }
  }

  // Show a loading state during the upload window WITHOUT touching the
  // dropzone's innerHTML — that would delete the <input type="file"> and
  // strip the file from the form before requestSubmit() serializes it
  // (which is exactly the bug this comment is replacing).
  //
  // Approach: overlay an absolutely-positioned spinner inside the dropzone,
  // dim the rest with opacity. The file input lives untouched and submits
  // the file as expected. Once the server redirects, Turbo swaps the page
  // out and the overlay is gone — only the failure path needs teardown.
  private showUploadingState(file: File) {
    const dropzone = this.dropzoneTarget

    // Anchor the overlay to the dropzone via position:relative without
    // disturbing existing styles (most dropzones aren't already positioned).
    if (getComputedStyle(dropzone).position === "static") {
      dropzone.style.position = "relative"
    }
    dropzone.classList.add("pointer-events-none", "cursor-wait")

    const overlay = document.createElement("div")
    overlay.dataset.fileUploadOverlay = "true"
    overlay.className =
      "absolute inset-0 flex flex-col items-center justify-center " +
      "space-y-3 bg-white/85 backdrop-blur-sm rounded-lg z-10"
    overlay.innerHTML = `
      <svg class="animate-spin h-8 w-8 text-default-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
      </svg>
      <div class="text-center">
        <p class="font-medium text-gray-900">Envoi du fichier…</p>
        <p class="text-xs text-gray-500 mt-0.5"></p>
      </div>
    `
    // Set filename via textContent so quirky names can't break out of HTML.
    const nameEl = overlay.querySelector("p.text-xs") as HTMLElement | null
    if (nameEl) nameEl.textContent = file.name

    dropzone.appendChild(overlay)

    const form = this.element.closest("form")
    if (form) {
      const teardown = (event: Event) => {
        const detail = (event as CustomEvent<{ success?: boolean }>).detail
        if (detail?.success) return // Turbo will navigate; leave the overlay.
        overlay.remove()
        dropzone.classList.remove("pointer-events-none", "cursor-wait")
        form.removeEventListener("turbo:submit-end", teardown)
      }
      form.addEventListener("turbo:submit-end", teardown)
    }
  }

  triggerFileInput() {
    this.inputTarget.click()
  }

  private get hasPreviewTarget(): boolean {
    return this.targets.has("preview")
  }

  private get hasFilenameTarget(): boolean {
    return this.targets.has("filename")
  }

  private get hasSubmitTarget(): boolean {
    return this.targets.has("submit")
  }
}
